import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import SEO from '../components/common/SEO';
import {
  CheckCircle, Clock, Truck, RotateCcw, FileText, Star,
  Package, CreditCard, Phone, MapPin, Calendar, ArrowLeft,
  Download, ExternalLink, AlertCircle, Euro, Check
} from 'lucide-react';
import { formatDate } from '../utils/dateUtils';
import { fetchDocLinks, openProtectedDoc } from '../utils/docLinks';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ─── 18 statuts avec icônes et descriptions client ────────────────────────
const STATUTS = {
  en_attente_paiement: { label: 'En attente de paiement',  color: '#9CA3AF', icon: <Clock size={14}/>,       desc: 'Votre réservation est enregistrée. Merci de finaliser le paiement.' },
  paiement_partiel:    { label: 'Acompte reçu ✓',          color: '#F59E0B', icon: <CreditCard size={14}/>,   desc: 'Votre acompte a bien été reçu. Le solde reste dû avant la livraison.' },
  payee:               { label: 'Paiement complet ✓',       color: '#10B981', icon: <CheckCircle size={14}/>, desc: 'Votre paiement est complet. Nous préparons votre réservation.' },
  contrat_envoye:      { label: 'Contrat envoyé',            color: '#5DBCBF', icon: <FileText size={14}/>,    desc: 'Votre contrat de location vous a été envoyé par email.' },
  contrat_signe:       { label: 'Contrat signé ✓',           color: '#1F9D7A', icon: <CheckCircle size={14}/>, desc: 'Votre contrat a été signé. Tout est prêt !' },
  a_preparer:          { label: 'En préparation',             color: '#8B5CF6', icon: <Package size={14}/>,     desc: 'Nous préparons votre matériel pour la livraison.' },
  produits_charges:    { label: 'Matériel chargé',            color: '#6366F1', icon: <Package size={14}/>,     desc: 'Votre matériel est chargé et prêt à partir.' },
  en_livraison:        { label: 'En route vers vous 🚚',     color: '#3B82F6', icon: <Truck size={14}/>,        desc: 'Notre équipe est en route pour vous livrer.' },
  livree:              { label: 'Livré ✓',                   color: '#22C55E', icon: <CheckCircle size={14}/>, desc: 'Votre matériel a été livré et installé. Profitez bien !' },
  en_location:         { label: 'En cours de location',      color: '#16A34A', icon: <CheckCircle size={14}/>, desc: 'Votre location est en cours. Nous venons récupérer le matériel à la date prévue.' },
  retour_a_effectuer:  { label: 'Retour prévu',              color: '#F97316', icon: <RotateCcw size={14}/>,   desc: 'Notre équipe vient récupérer le matériel selon les horaires convenus.' },
  retour_effectue:     { label: 'Matériel récupéré ✓',       color: '#D4AF37', icon: <Check size={14}/>,       desc: 'Le matériel a bien été récupéré. Contrôle en cours.' },
  retour_anomalie:     { label: 'Retour signalé',             color: '#EF4444', icon: <AlertCircle size={14}/>, desc: 'Une anomalie a été constatée lors du retour. Nous allons vous contacter.' },
  controle_termine:    { label: 'Contrôle terminé ✓',        color: '#0EA5E9', icon: <CheckCircle size={14}/>, desc: 'Le contrôle du matériel est terminé.' },
  facture_envoyee:     { label: 'Facture envoyée',            color: '#6B7280', icon: <FileText size={14}/>,    desc: 'Votre facture a été envoyée par email.' },
  avis_demande:        { label: 'Avis demandé',               color: '#C58B19', icon: <Star size={14}/>,        desc: 'Votre avis nous a été demandé. Merci de partager votre expérience !' },
  terminee:            { label: 'Location terminée ✓',        color: '#374151', icon: <CheckCircle size={14}/>, desc: 'Votre location est terminée. Merci pour votre confiance !' },
  annulee:             { label: 'Annulée',                    color: '#DC2626', icon: <AlertCircle size={14}/>, desc: 'Cette réservation a été annulée.' },
  // Anciens statuts
  brouillon:    { label: 'En attente',       color: '#9CA3AF', icon: <Clock size={14}/> },
  envoye:       { label: 'Envoyé',           color: '#5DBCBF', icon: <FileText size={14}/> },
  signe:        { label: 'Signé ✓',          color: '#1F9D7A', icon: <CheckCircle size={14}/> },
  acompte_paye: { label: 'Acompte payé ✓',  color: '#F59E0B', icon: <CreditCard size={14}/> },
  solde_paye:   { label: 'Payé ✓',           color: '#10B981', icon: <CheckCircle size={14}/> },
  livre:        { label: 'Livré ✓',          color: '#22C55E', icon: <CheckCircle size={14}/> },
  termine:      { label: 'Terminé ✓',        color: '#374151', icon: <CheckCircle size={14}/> },
  annule:       { label: 'Annulée',          color: '#DC2626', icon: <AlertCircle size={14}/> },
};

// Étapes visibles du parcours (ordre)
const ETAPES = [
  { statuts: ['en_attente_paiement','brouillon','envoye'],        label: 'Réservation',  icon: <FileText size={14}/> },
  { statuts: ['paiement_partiel','signe','acompte_paye'],         label: 'Acompte',      icon: <CreditCard size={14}/> },
  { statuts: ['payee','solde_paye','contrat_envoye','contrat_signe'], label: 'Confirmé', icon: <CheckCircle size={14}/> },
  { statuts: ['a_preparer','produits_charges'],                   label: 'Préparation',  icon: <Package size={14}/> },
  { statuts: ['en_livraison','livree','en_location'],             label: 'Livraison',    icon: <Truck size={14}/> },
  { statuts: ['retour_a_effectuer','retour_effectue','retour_anomalie','controle_termine'], label: 'Retour', icon: <RotateCcw size={14}/> },
  { statuts: ['facture_envoyee','avis_demande','terminee','termine'], label: 'Terminé',  icon: <Star size={14}/> },
];

function getEtapeIndex(statut) {
  for (let i = 0; i < ETAPES.length; i++) {
    if (ETAPES[i].statuts.includes(statut)) return i;
  }
  return 0;
}

export default function MaReservationPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusHistory, setStatusHistory] = useState([]);
  const [etat, setEtat] = useState(null);
  const [factures, setFactures] = useState([]);
  const [docLinks, setDocLinks] = useState(null);
  const [showAvisForm, setShowAvisForm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [docError, setDocError] = useState(false);

  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');

  // ── ANO-ECC-04 : ouverture de document via lien SIGNÉ (durée 30 min) ─────────
  // Les liens signés sont régénérés à chaque chargement. Si un lien a expiré
  // (onglet resté ouvert > 30 min), on régénère silencieusement puis on réessaie ;
  // en dernier recours on affiche un message « lien expiré » avec un bouton.
  async function refreshDocLinks() {
    const tParam = new URLSearchParams(window.location.search).get('t') || undefined;
    const tok = token || undefined;
    const fresh = await fetchDocLinks(BACKEND, id, { t: tParam, token: tok });
    if (fresh) { setDocLinks(fresh); setDocError(false); return fresh; }
    setDocError(true);
    return null;
  }

  async function openDoc(path) {
    if (!path) { setDocError(true); return; }
    const tok = token || undefined;
    if (await openProtectedDoc(BACKEND, path, tok)) return;
    // lien signé probablement expiré → régénérer puis réessayer une fois
    const fresh = await refreshDocLinks();
    if (!fresh) return; // bannière affichée
    const candidates = [fresh.devis, fresh.facture, fresh.contrat, ...(fresh.factures || []).map(f => f.url)].filter(Boolean);
    const match = candidates.find(u => u.split('?')[0] === path.split('?')[0]);
    if (match && await openProtectedDoc(BACKEND, match, tok)) return;
    setDocError(true);
  }

  async function cancelReservation() {
    if (!token) { alert('Connectez-vous à votre compte pour annuler votre réservation.'); return; }
    const H = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
    try {
      const pv = await (await fetch(`${BACKEND}/api/devis/${id}/annulation-client-preview`, { headers: H })).json();
      const ok = window.confirm(
        `Annuler votre réservation ${quote.numero} ?\n\n` +
        `Montant payé : ${(pv.total_paye || 0).toFixed(2)}€\n` +
        `Caution conservée (${pv.caution_pct}%) : ${(pv.caution_retenue || 0).toFixed(2)}€\n` +
        `Montant remboursé : ${(pv.montant_rembourse || 0).toFixed(2)}€\n\n` +
        `Cette action est définitive. Confirmer l'annulation ?`
      );
      if (!ok) return;
      setCancelling(true);
      const res = await fetch(`${BACKEND}/api/devis/${id}/annuler-client`, {
        method: 'POST', headers: H, body: JSON.stringify({}),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Erreur');
      alert(`Réservation annulée.\nMontant remboursé : ${(d.montant_rembourse_total || 0).toFixed(2)}€ (caution de ${d.caution_pct}% conservée).`);
      window.location.reload();
    } catch (e) { alert(`Erreur : ${e.message}`); setCancelling(false); }
  }

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${BACKEND}/api/devis/${id}`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        setQuote(data);

        // État de paiement RÉEL (calcul serveur — corrige ANO-ECC-01/02) + factures distinctes (ANO-ECC-06)
        try {
          const tParam = new URLSearchParams(window.location.search).get('t') || undefined;
          const tok = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth') || undefined;
          const [eRes, fRes, links] = await Promise.all([
            fetch(`${BACKEND}/api/devis/${id}/paiement-etat`),
            fetch(`${BACKEND}/api/devis/${id}/factures`),
            fetchDocLinks(BACKEND, id, { t: tParam, token: tok }),
          ]);
          if (eRes.ok) setEtat(await eRes.json());
          if (fRes.ok) setFactures((await fRes.json()).factures || []);
          if (links) setDocLinks(links);
        } catch { /* non bloquant */ }

        // Historique des statuts
        const hRes = await fetch(`${BACKEND}/api/booking/order/${id}/status-history`);
        if (hRes.ok) {
          const hData = await hRes.json();
          setStatusHistory(hData.history || []);
        }
      } catch { navigate('/mon-compte'); }
      setLoading(false);
    }
    load();
  }, [id, navigate]);

  if (loading) {
    return (
      <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="animate-spin w-10 h-10 rounded-full mx-auto"
            style={{ border: '3px solid var(--border)', borderTopColor: 'var(--teal)' }} />
        </div>
      </div>
    );
  }

  if (!quote) return null;

  const statut = quote.statut || 'brouillon';
  const statutConf = STATUTS[statut] || STATUTS.brouillon;
  const etapeActuelle = getEtapeIndex(statut);
  const sess = quote.session || {};
  const dateEv = quote.date_livraison || sess.date_livraison || sess.date_evenement || '';
  const dateRet = quote.date_reprise || sess.date_reprise || '';
  const heureLiv = quote.heure_livraison || sess.heure_livraison || '09:00';
  const heureRet = quote.heure_retour || sess.heure_retour || '10:00';
  const total = etat ? etat.total : (quote.total || 0);
  // Montants et état de règlement RÉELS (serveur) — corrige ANO-ECC-01 (« payé » à tort) et ANO-ECC-02 (recalcul front)
  const acompte = etat ? etat.acompte : (quote.acompte != null ? quote.acompte : Math.round((quote.total || 0) * 0.30 * 100) / 100);
  const solde = etat ? etat.solde : (quote.solde != null ? quote.solde : Math.round((quote.total || 0) * 0.70 * 100) / 100);
  const acompteRegle = etat ? etat.acompte_regle : false;
  const soldeRegle = etat ? etat.solde_regle : false;
  const resteDu = etat ? etat.reste_du : solde;
  const LOCKED_STATUTS = ['annule','annulee','terminee','termine','en_livraison','livree',
    'en_location','retour_a_effectuer','retour_effectue','retour_anomalie',
    'controle_termine','facture_envoyee','avis_demande'];
  const canCancel = !!token && !LOCKED_STATUTS.includes(statut);

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <SEO
        title={`Ma réservation ${quote.numero} — Show Événement`}
        canonical={`https://show-evenement.com/ma-reservation/${id}`}
        noindex
      />
      <Header />

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Retour */}
        <Link to="/mon-compte" className="flex items-center gap-1.5 text-sm mb-6"
          style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>
          <ArrowLeft size={14} /> Mes réservations
        </Link>

        {/* Header réservation */}
        <div className="rounded-2xl p-5 mb-5"
          style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold mb-1" style={{ color: 'var(--ink)' }}>
                Réservation {quote.numero}
              </h1>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-semibold"
                  style={{ background: `${statutConf.color}15`, color: statutConf.color }}>
                  {statutConf.icon} {statutConf.label}
                </span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold price-gold">{total}€</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Total TTC</p>
            </div>
          </div>

          {/* Description du statut */}
          {statutConf.desc && (
            <div className="p-3 rounded-xl"
              style={{ background: `${statutConf.color}08`, border: `1px solid ${statutConf.color}25` }}>
              <p className="text-sm" style={{ color: 'var(--ink)', lineHeight: 1.6 }}>
                {statutConf.desc}
              </p>
            </div>
          )}
        </div>

        {/* Progression étapes */}
        <div className="rounded-2xl p-5 mb-5"
          style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
          <h2 className="text-sm font-bold mb-4" style={{ color: 'var(--ink)' }}>Suivi de votre réservation</h2>
          <div className="relative">
            {/* Ligne de connexion */}
            <div className="absolute left-4 top-4 bottom-4 w-0.5"
              style={{ background: 'var(--border)', zIndex: 0 }} />
            <div className="absolute left-4 top-4 w-0.5 transition-all"
              style={{
                background: 'var(--teal)',
                height: `${(etapeActuelle / (ETAPES.length - 1)) * 100}%`,
                zIndex: 1,
              }} />
            <div className="space-y-4 relative z-10">
              {ETAPES.map((etape, i) => {
                const done    = i < etapeActuelle;
                const current = i === etapeActuelle;
                const future  = i > etapeActuelle;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        background: done || current ? 'var(--teal)' : '#FFFFFF',
                        border: `2px solid ${done || current ? 'var(--teal)' : 'var(--border)'}`,
                        zIndex: 2,
                      }}>
                      {done ? (
                        <Check size={14} style={{ color: '#0B1E20' }} />
                      ) : (
                        <span style={{ color: done || current ? '#0B1E20' : 'var(--text-muted)', transform: 'scale(0.85)' }}>
                          {etape.icon}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium"
                      style={{ color: future ? 'var(--text-muted)' : 'var(--ink)', opacity: future ? 0.5 : 1 }}>
                      {etape.label}
                      {current && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full"
                          style={{ background: 'var(--teal)', color: '#0B1E20', fontWeight: 700 }}>
                          En cours
                        </span>
                      )}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Détails de la location */}
        <div className="rounded-2xl p-5 mb-5"
          style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
          <h2 className="text-sm font-bold mb-4" style={{ color: 'var(--ink)' }}>Détails de votre location</h2>
          <div className="space-y-3">
            {dateEv && (
              <div className="flex items-start gap-3">
                <Calendar size={15} style={{ color: 'var(--teal)', flexShrink: 0, marginTop: 1 }} />
                <div>
                  <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Livraison</p>
                  <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
                    {formatDate(dateEv)} à {heureLiv}
                  </p>
                </div>
              </div>
            )}
            {dateRet && (
              <div className="flex items-start gap-3">
                <RotateCcw size={15} style={{ color: '#F97316', flexShrink: 0, marginTop: 1 }} />
                <div>
                  <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Récupération</p>
                  <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
                    {formatDate(dateRet)} à {heureRet}
                  </p>
                </div>
              </div>
            )}
            {(quote.adresse_livraison || sess.adresse) && (
              <div className="flex items-start gap-3">
                <MapPin size={15} style={{ color: 'var(--teal)', flexShrink: 0, marginTop: 1 }} />
                <div>
                  <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Adresse de livraison</p>
                  <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
                    {quote.adresse_livraison || sess.adresse}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Articles */}
          {(quote.items || []).length > 0 && (
            <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
              <p className="text-xs font-bold mb-3" style={{ color: 'var(--text-muted)' }}>Matériel loué</p>
              <div className="space-y-2">
                {quote.items.map((item, i) => (
                  <div key={i} className="flex justify-between items-center text-sm">
                    <span style={{ color: 'var(--ink)' }}>{item.product_nom} × {item.quantite}</span>
                    <span className="font-semibold" style={{ color: 'var(--ink)' }}>{item.sous_total || 0}€</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Paiement */}
        <div className="rounded-2xl p-5 mb-5"
          style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
          <h2 className="text-sm font-bold mb-4" style={{ color: 'var(--ink)' }}>Paiement</h2>
          <div className="space-y-2">
            <div className="flex justify-between items-center p-3 rounded-xl" data-testid="paiement-acompte-ligne"
              style={{ background: acompteRegle ? 'rgba(34,197,94,0.06)' : 'var(--bg)', border: '1px solid var(--border)' }}>
              <div className="flex items-center gap-2">
                {acompteRegle ? <CheckCircle size={14} style={{ color: '#22C55E' }} /> : <Clock size={14} style={{ color: 'var(--text-muted)' }} />}
                <span className="text-sm" style={{ color: 'var(--ink)' }}>Acompte 30%</span>
              </div>
              <span className="font-bold text-sm" style={{ color: acompteRegle ? '#22C55E' : 'var(--text-muted)' }}>
                {acompteRegle ? `${acompte.toFixed(2)}€ payé ✓` : `${acompte.toFixed(2)}€ à régler`}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 rounded-xl" data-testid="paiement-solde-ligne"
              style={{ background: soldeRegle ? 'rgba(34,197,94,0.06)' : 'var(--bg)', border: '1px solid var(--border)' }}>
              <div className="flex items-center gap-2">
                {soldeRegle ? <CheckCircle size={14} style={{ color: '#22C55E' }} /> : <Clock size={14} style={{ color: 'var(--text-muted)' }} />}
                <span className="text-sm" style={{ color: 'var(--ink)' }}>Solde 70%</span>
              </div>
              <span className="font-bold text-sm" style={{ color: soldeRegle ? '#22C55E' : '#F59E0B' }}>
                {soldeRegle ? `${solde.toFixed(2)}€ payé ✓` : `${resteDu.toFixed(2)}€ restant`}
              </span>
            </div>
            {/* Bouton payer si non soldé */}
            {!soldeRegle && (
              <Link to={`/devis/${id}?paiement=solde`} data-testid="paiement-payer-solde-btn"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm mt-2"
                style={{ background: '#1F9D7A', color: '#FFFFFF', textDecoration: 'none', display: 'flex' }}>
                <CreditCard size={15} /> {acompteRegle ? `Payer le solde ${resteDu.toFixed(2)}€` : `Payer l'acompte ${acompte.toFixed(2)}€`}
              </Link>
            )}
          </div>
        </div>

        {/* Remboursements (§1.7) — historique visible par le client */}
        {etat && etat.remboursements && etat.remboursements.length > 0 && (
          <div className="rounded-2xl p-5 mb-5" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }} data-testid="remboursements-section">
            <h2 className="text-sm font-bold mb-3" style={{ color: 'var(--ink)' }}>Remboursements</h2>
            <div className="space-y-2">
              {etat.remboursements.map((rb, i) => (
                <div key={i} className="flex justify-between items-center p-3 rounded-xl" data-testid={`remboursement-${i}`}
                  style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <div className="flex items-center gap-2">
                    {rb.statut === 'rembourse'
                      ? <CheckCircle size={14} style={{ color: '#22C55E' }} />
                      : <Clock size={14} style={{ color: '#F59E0B' }} />}
                    <div>
                      <span className="text-sm" style={{ color: 'var(--ink)' }}>
                        {rb.mode === 'carte' ? 'Remboursement sur carte' : 'Remboursement par virement'}
                      </span>
                      {rb.date && <span className="text-xs block" style={{ color: 'var(--text-muted)' }}>{formatDate(new Date(rb.date))}</span>}
                    </div>
                  </div>
                  <span className="font-bold text-sm" style={{ color: rb.statut === 'rembourse' ? '#22C55E' : '#F59E0B' }}>
                    {Number(rb.montant || 0).toFixed(2)}€ {rb.statut === 'rembourse' ? 'remboursé ✓' : 'en cours'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="rounded-2xl p-5 mb-5"
          style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
          <h2 className="text-sm font-bold mb-4" style={{ color: 'var(--ink)' }}>Documents</h2>
          <div className="space-y-2">
            {docLinks && docLinks.devis && (
              <button onClick={() => openDoc(docLinks.devis)} data-testid="doc-devis"
                className="w-full flex items-center gap-2 p-3 rounded-xl text-sm font-medium"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)', cursor: 'pointer', textAlign: 'left' }}>
                <Download size={14} style={{ color: 'var(--teal)' }} /> Télécharger mon devis (PDF)
              </button>
            )}
            {/* Factures DISTINCTES (acompte / finale) — ANO-ECC-06, liens signés ANO-ECC-04 */}
            {docLinks && (docLinks.factures || []).map((fac) => (
              <button key={fac.id} onClick={() => openDoc(fac.url)}
                data-testid={`facture-lien-${fac.type}`}
                className="w-full flex items-center gap-2 p-3 rounded-xl text-sm font-medium"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)', cursor: 'pointer', textAlign: 'left' }}>
                <Download size={14} style={{ color: '#D4AF37' }} /> {({ acompte: "Facture d'acompte", finale: 'Facture finale', caution: "Facture frais d'annulation", avoir: 'Avoir' }[fac.type] || 'Facture')} {fac.numero} (PDF)
              </button>
            ))}
            {/* Repli : ancienne facture unique si aucune facture distincte encore émise */}
            {docLinks && (!docLinks.factures || docLinks.factures.length === 0) && acompteRegle && (
              <button onClick={() => openDoc(docLinks.facture)} data-testid="facture-lien-legacy"
                className="w-full flex items-center gap-2 p-3 rounded-xl text-sm font-medium"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)', cursor: 'pointer', textAlign: 'left' }}>
                <Download size={14} style={{ color: '#D4AF37' }} /> Télécharger ma facture (PDF)
              </button>
            )}
            {docLinks && docLinks.contrat && (
              <button onClick={() => openDoc(docLinks.contrat)} data-testid="doc-contrat"
                className="w-full flex items-center gap-2 p-3 rounded-xl text-sm font-medium"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)', cursor: 'pointer', textAlign: 'left' }}>
                <ExternalLink size={14} style={{ color: '#8B5CF6' }} /> Voir mon contrat de location
              </button>
            )}
            {/* ANO-ECC-04 : lien signé expiré → régénération à la demande (pas d'erreur brute) */}
            {docError && (
              <div className="p-3 rounded-xl" data-testid="doc-lien-expire"
                style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.3)' }}>
                <p className="text-sm mb-2" style={{ color: '#B91C1C' }}>
                  Ce lien de document a expiré pour des raisons de sécurité.
                </p>
                <button onClick={refreshDocLinks} data-testid="doc-refresh-btn"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold"
                  style={{ background: 'var(--teal)', color: '#fff', cursor: 'pointer' }}>
                  <RotateCcw size={14} /> Générer un nouveau lien
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Annuler ma réservation (remboursement sauf caution 30%) */}
        {canCancel && (
          <div className="rounded-2xl p-5 mb-5"
            style={{ background: '#FFFFFF', border: '1px solid rgba(220,38,38,0.2)' }}>
            <h2 className="text-sm font-bold mb-2" style={{ color: 'var(--ink)' }}>Annuler ma réservation</h2>
            <p className="text-xs mb-3" style={{ color: 'var(--text-muted)', lineHeight: 1.6 }}>
              Si vous annulez, une caution de <strong>30%</strong> du total est conservée ;
              le reste des sommes déjà versées vous est remboursé. Les créneaux sont libérés.
            </p>
            <button onClick={cancelReservation} disabled={cancelling} data-testid="client-cancel-reservation-btn"
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm disabled:opacity-50"
              style={{ background: 'rgba(220,38,38,0.08)', color: '#DC2626', border: '1px solid rgba(220,38,38,0.3)' }}>
              <AlertCircle size={15} /> {cancelling ? 'Annulation…' : 'Annuler ma réservation'}
            </button>
          </div>
        )}

        {/* Avis si terminé */}
        {['terminee','avis_demande','facture_envoyee','termine'].includes(statut) && (
          <div className="rounded-2xl p-5 mb-5"
            style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.3)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Star size={16} style={{ color: '#D4AF37' }} />
              <h2 className="text-sm font-bold" style={{ color: 'var(--ink)' }}>Votre avis compte !</h2>
            </div>
            <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
              Votre expérience aide les autres clients à choisir. Laissez-nous un avis Google !
            </p>
            <a href="https://g.page/r/show-evenement/review" target="_blank" rel="noreferrer"
              className="flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm"
              style={{ background: '#D4AF37', color: '#0B1E20', textDecoration: 'none', display: 'flex' }}>
              <Star size={14} /> Laisser un avis Google
            </a>
          </div>
        )}

        {/* Contact */}
        <div className="text-center py-4">
          <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>Une question sur votre réservation ?</p>
          <Link to="/contact"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold"
            style={{ background: 'var(--teal-mist)', color: '#0B4B4E', border: '1px solid rgba(93,188,191,0.3)', textDecoration: 'none' }}>
            <Phone size={14} /> Nous contacter
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
